import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Crown,
  Zap,
  Calendar,
  Shield,
  CreditCard,
  Download,
  History,
  X,
  Star,
  AlertTriangle,
  Clock,
  FileText,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import plansService, {
  type UserSubscription,
  type Plan,
} from "@/services/plansService";
import stripeService, { type StripeInvoice } from "@/services/stripeService";
import { captureFatalException } from "@/config/sentry";
import { AiUsageCard } from "@/components/ai/AiUsageCard";
import { AiCreditsSection } from "@/components/ai/credits/AiCreditsSection";
import { cn } from "@/utils/cn";

// Types
type UserData = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  isEmailVerified: boolean;
  subscriptionPlan: string | null;
  subscriptionStatus: string | null;
  subscriptionEndDate: string | null;
  createdAt: string;
  updatedAt: string;
};

interface SubscriptionTabProps {
  user: UserData | null;
}

export const SubscriptionTab: React.FC<SubscriptionTabProps> = ({ user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // State management
  const [subscription, setSubscription] =
    React.useState<UserSubscription | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = React.useState(false);
  const [isUpdatingSubscription, setIsUpdatingSubscription] =
    React.useState(false);
  const [isReactivatingSubscription, setIsReactivatingSubscription] =
    React.useState(false);
  const [invoices, setInvoices] = React.useState<StripeInvoice[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = React.useState(false);

  // New states for upgrade functionality
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = React.useState(false);
  const [availableUpgradePlans, setAvailableUpgradePlans] = React.useState<
    Plan[]
  >([]);
  const [isLoadingUpgradePlans, setIsLoadingUpgradePlans] =
    React.useState(false);

  const [selectedUpgradePlan, setSelectedUpgradePlan] =
    React.useState<Plan | null>(null);

  // States for proration confirmation
  const [isProrationDialogOpen, setIsProrationDialogOpen] =
    React.useState(false);
  const [prorationDetails, setProrationDetails] = React.useState<any>(null);
  const [isProcessingUpgrade, setIsProcessingUpgrade] = React.useState(false);
  const [isLoadingProration, setIsLoadingProration] = React.useState(false);

  // Billing history dialog state
  const [isBillingHistoryDialogOpen, setIsBillingHistoryDialogOpen] =
    React.useState(false);

  // Load subscription data on component mount
  React.useEffect(() => {
    const loadSubscriptionData = async () => {
      try {
        setIsLoading(true);
        const subscriptionData = await plansService.getUserSubscription();

        setSubscription(subscriptionData);
      } catch (error) {
        console.error("Error loading subscription data:", error);
        toast.error(t("subscription.dashboard.errorLoadingData"));
      } finally {
        setIsLoading(false);
      }
    };

    loadSubscriptionData();
  }, [t]);

  // Les factures sont une section visible de la page : chargées dès que le
  // customer Stripe est connu (plus seulement à l'ouverture du dialog).
  React.useEffect(() => {
    if (subscription?.stripeCustomerId) {
      loadInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription?.stripeCustomerId]);

  // Handle upgrade success/cancel messages from URL params
  React.useEffect(() => {
    const upgraded = searchParams.get("upgraded");
    const upgradeCancelled = searchParams.get("upgrade_cancelled");

    if (upgraded === "true") {
      toast.success("Mise à niveau réussie !", {
        description:
          "Votre abonnement a été mis à niveau avec succès. Les nouveaux avantages sont maintenant actifs.",
        duration: 6000,
      });

      // Clean up URL params
      setSearchParams((params) => {
        params.delete("upgraded");
        params.delete("session_id");
        return params;
      });

      // Refresh subscription data after a short delay to ensure webhook processing is complete
      setTimeout(async () => {
        try {
          const updatedSubscription = await plansService.getUserSubscription();
          setSubscription(updatedSubscription);
        } catch (error) {
          console.error(
            "Error refreshing subscription data after upgrade:",
            error,
          );
        }
      }, 2000);
    }

    if (upgradeCancelled === "true") {
      toast.info("Mise à niveau annulée", {
        description:
          "Vous avez annulé la mise à niveau. Votre abonnement actuel reste inchangé.",
        duration: 4000,
      });

      // Clean up URL params
      setSearchParams((params) => {
        params.delete("upgrade_cancelled");
        return params;
      });
    }
  }, [searchParams, setSearchParams]);

  // Function to load upgrade plans
  const loadUpgradePlans = async () => {
    if (!user?.subscriptionPlan) {
      return;
    }

    try {
      setIsLoadingUpgradePlans(true);

      // Get all plans
      const allPlans = await plansService.getPlans();

      // Find current plan
      const currentPlan = allPlans.find(
        (plan) => plan.name === user.subscriptionPlan,
      );

      if (!currentPlan) {
        setAvailableUpgradePlans([]);
        return;
      }

      // Filter plans with higher display order (superior plans)
      const upgradePlans = allPlans
        .filter(
          (plan) =>
            plan.isActive &&
            plan.displayOrder > currentPlan.displayOrder &&
            plan.priceId, // Ensure plan has Stripe integration
        )
        .sort((a, b) => a.displayOrder - b.displayOrder);

      setAvailableUpgradePlans(upgradePlans);
    } catch (error) {
      console.error("Error loading upgrade plans:", error);
      toast.error(t("subscription.dashboard.loadingUpgradePlansError"));
      setAvailableUpgradePlans([]);
    } finally {
      setIsLoadingUpgradePlans(false);
    }
  };

  // Function to handle upgrade plan selection - preview proration and confirm
  const handleSelectUpgradePlan = async (plan: Plan) => {
    if (!plan.priceId) {
      toast.error(t("subscription.dashboard.planNotAvailableForUpgrade"));
      return;
    }

    // Validate required data
    if (!subscription?.stripeSubscriptionId) {
      toast.error(
        "Impossible de trouver votre abonnement actuel. Veuillez contacter le support.",
      );
      return;
    }

    setSelectedUpgradePlan(plan);
    setIsLoadingProration(true);

    try {
      // First, preview the proration without executing the upgrade
      const prorationPreview = await stripeService.previewUpgradeProration({
        subscriptionId: subscription.stripeSubscriptionId,
        newPriceId: plan.priceId,
      });

      setProrationDetails(prorationPreview);
      setIsProrationDialogOpen(true);
    } catch (error) {
      console.error("Error loading proration preview:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Erreur lors du calcul de la proration",
      );
    } finally {
      setIsLoadingProration(false);
    }
  };

  // Function to confirm and execute the upgrade after proration preview
  const handleConfirmUpgrade = async () => {
    if (!subscription?.stripeSubscriptionId || !selectedUpgradePlan?.priceId) {
      toast.error("Données manquantes pour effectuer l'upgrade.");
      return;
    }

    setIsProcessingUpgrade(true);

    try {
      // Execute the actual upgrade
      const upgradeResult = await stripeService.upgradeSubscriptionSimple({
        subscriptionId: subscription.stripeSubscriptionId,
        newPriceId: selectedUpgradePlan.priceId,
      });

      // Check if payment is required
      if (upgradeResult.proration_preview.amount_due > 0) {
        // Payment is required - redirect to Customer Portal for immediate payment
        if (!subscription?.stripeCustomerId) {
          toast.error("Impossible de trouver votre ID client Stripe.");
          return;
        }

        toast.info("Redirection vers le portail de paiement...", {
          description: `Vous allez être redirigé pour payer ${(
            (upgradeResult.proration_preview.amount_due || 0) / 100
          ).toFixed(2)} € immédiatement.`,
          duration: 3000,
        });

        // Small delay to show the toast
        setTimeout(async () => {
          if (subscription?.stripeCustomerId) {
            try {
              const { url } = await stripeService.createCustomerPortalSession(
                subscription.stripeCustomerId,
                `${window.location.origin}/dashboard?upgraded=true`,
              );

              if (!url) {
                // ✅ ERREUR FATALE : Pas d'URL de portail
                captureFatalException(
                  new Error(
                    "Customer portal session created but no URL returned",
                  ),
                  {
                    operation: "create_customer_portal_session",
                    customerId: subscription.stripeCustomerId,
                    userId: user?.id,
                    userEmail: user?.email,
                    requires_immediate_attention: true,
                    impact: "user_cannot_access_payment_portal",
                    financial_impact: true,
                  },
                );
                toast.error("Impossible d'accéder au portail de paiement.");
                return;
              }

              window.location.href = url;
            } catch (error) {
              // ✅ ERREUR FATALE : Échec de création de session portail
              captureFatalException(error as Error, {
                operation: "create_customer_portal_session",
                customerId: subscription.stripeCustomerId,
                userId: user?.id,
                userEmail: user?.email,
                requires_immediate_attention: true,
                impact: "payment_portal_access_blocked",
                financial_impact: true,
              });

              console.error("Failed to create customer portal session:", error);
              toast.error("Erreur lors de l'accès au portail de paiement.");
            }
          }
        }, 1000);
      } else {
        // No payment required, upgrade is complete
        setIsProrationDialogOpen(false);
        toast.success("Mise à niveau réussie !", {
          description:
            "Votre abonnement a été mis à niveau avec succès sans paiement supplémentaire.",
          duration: 6000,
        });

        // Refresh subscription data
        setTimeout(async () => {
          try {
            const updatedSubscription =
              await plansService.getUserSubscription();
            setSubscription(updatedSubscription);
          } catch (error) {
            console.error("Error refreshing subscription:", error);
          }
        }, 2000);
      }
    } catch (error) {
      // ✅ ERREUR FATALE : Échec d'upgrade d'abonnement
      captureFatalException(error as Error, {
        operation: "upgrade_subscription",
        subscriptionId: subscription?.stripeSubscriptionId,
        newPriceId: selectedUpgradePlan?.priceId,
        planName: selectedUpgradePlan?.name,
        userId: user?.id,
        userEmail: user?.email,
        requires_immediate_attention: true,
        impact: "subscription_upgrade_failed",
        financial_impact: true,
      });

      console.error("Error executing upgrade:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Erreur lors de l'exécution de la mise à niveau",
      );
    } finally {
      setIsProcessingUpgrade(false);
    }
  };

  // Action handlers
  const handleUpgradePlan = async () => {
    await loadUpgradePlans();
    setIsUpgradeDialogOpen(true);
  };

  const handleManageBilling = async () => {
    if (!subscription?.stripeCustomerId) {
      toast.error(t("subscription.dashboard.noCustomerIdError"));
      return;
    }

    try {
      const { url } = await stripeService.createCustomerPortalSession(
        subscription.stripeCustomerId!,
        window.location.href,
      );
      window.location.href = url;
    } catch (error) {
      console.error("Error creating customer portal session:", error);
      toast.error(t("subscription.dashboard.errorCreatingPortalSession"));
    }
  };

  const handleUpdatePayment = async () => {
    // Redirect to customer portal for payment method management
    await handleManageBilling();
  };

  const handleDownloadInvoice = async (invoiceId?: string) => {
    if (!invoiceId) {
      // Load latest invoice
      await loadInvoices();
      if (invoices.length === 0) {
        toast.error(t("subscription.dashboard.noInvoicesFound"));
        return;
      }
      invoiceId = invoices[0].id;
    }

    try {
      const { pdf_url } = await stripeService.getInvoicePdf(invoiceId);
      window.open(pdf_url, "_blank");
      toast.success(t("subscription.dashboard.invoiceDownloadStarted"));
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error(t("subscription.dashboard.errorDownloadingInvoice"));
    }
  };

  const handleViewBillingHistory = async () => {
    await loadInvoices();
    setIsBillingHistoryDialogOpen(true);
  };

  // Load customer invoices
  const loadInvoices = async () => {
    if (!subscription?.stripeCustomerId || isLoadingInvoices) {
      return;
    }

    try {
      setIsLoadingInvoices(true);
      const customerInvoices = await stripeService.getCustomerInvoices(
        subscription.stripeCustomerId!,
        10,
      );
      setInvoices(customerInvoices);
    } catch (error) {
      console.error("Error loading invoices:", error);
      toast.error(t("subscription.dashboard.errorLoadingInvoices"));
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setIsUpdatingSubscription(true);
      const result = await plansService.cancelSubscription();

      // Show the message returned by the backend (already formatted appropriately)
      toast.success(result.message);

      // Close dialog immediately
      setIsCancelDialogOpen(false);

      // Refresh subscription data after a short delay to allow webhook processing
      setTimeout(async () => {
        try {
          const updatedSubscription = await plansService.getUserSubscription();

          setSubscription(updatedSubscription);
        } catch (refreshError) {
          console.error("Error refreshing subscription data:", refreshError);
          // Don't show error to user as the cancellation was successful
        }
      }, 3000); // Increased to 3 seconds to give webhooks more time
    } catch (error) {
      console.error("Error cancelling subscription:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("subscription.dashboard.errorCancellingSubscription"),
      );
    } finally {
      setIsUpdatingSubscription(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setIsReactivatingSubscription(true);
      const result = await plansService.reactivateSubscription();

      // Show the message returned by the backend
      toast.success(result.message);

      // Refresh subscription data after a short delay to allow webhook processing
      setTimeout(async () => {
        try {
          const updatedSubscription = await plansService.getUserSubscription();
          setSubscription(updatedSubscription);
        } catch (refreshError) {
          console.error("Error refreshing subscription data:", refreshError);
          // Don't show error to user as the reactivation was successful
        }
      }, 3000); // 3 seconds to give webhooks time to process
    } catch (error) {
      console.error("Error reactivating subscription:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : t("subscription.dashboard.reactivationError"),
      );
    } finally {
      setIsReactivatingSubscription(false);
    }
  };

  // Helper functions
  const getSubscriptionStatusColor = (status: string | null | undefined) => {
    switch (status) {
      case "active":
        return "bg-green-500";
      case "trialing":
        return "bg-blue-500";
      case "pending":
        return "bg-yellow-500";
      case "canceled":
        return "bg-red-500";
      case "expired":
        return "bg-gray-500";
      default:
        return "bg-gray-400";
    }
  };

  // Helper function to check if subscription is in trial period
  const isInTrialPeriod = () => {
    if (!subscription?.trialEnd) return false;
    const now = new Date();
    const trialEnd = new Date(subscription.trialEnd);
    return now < trialEnd && subscription.status === "trialing";
  };

  // Helper function to get remaining trial days
  const getRemainingTrialDays = () => {
    if (!subscription?.trialEnd) return 0;
    const now = new Date();
    const trialEnd = new Date(subscription.trialEnd);
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  // New helper function to detect if subscription is set to cancel at period end
  const isSubscriptionCancellingAtPeriodEnd = () => {
    return (
      subscription?.status === "active" &&
      subscription?.cancelAtPeriodEnd === true
    );
  };

  // Helper function to get effective subscription status for display
  const getEffectiveSubscriptionStatus = () => {
    if (isSubscriptionCancellingAtPeriodEnd()) {
      return "cancelling";
    }
    if (isInTrialPeriod()) {
      return "trialing";
    }
    return subscription?.status || user?.subscriptionStatus;
  };

  // Updated status color function to handle cancelling and trial states
  const getEffectiveSubscriptionStatusColor = () => {
    if (isSubscriptionCancellingAtPeriodEnd()) {
      return "bg-orange-500";
    }
    if (isInTrialPeriod()) {
      return "bg-blue-500";
    }
    return getSubscriptionStatusColor(
      subscription?.status || user?.subscriptionStatus,
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateLong = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getInvoiceDisplayNumber = (invoice: StripeInvoice) => {
    if (invoice.number) return invoice.number;
    return `INV-${invoice.id.substring(0, 8).toUpperCase()}`;
  };

  // Show loading skeleton — same shape as the loaded page to prevent content jumping
  if (isLoading) {
    return (
      <div
        className="mx-auto w-full max-w-6xl space-y-6 p-4 md:p-6"
        aria-busy="true"
        aria-live="polite"
      >
        {/* Header */}
        <div className="space-y-2">
          <div className="h-3.5 w-36 bg-muted rounded animate-pulse" />
          <div className="h-8 w-44 bg-muted rounded animate-pulse" />
        </div>

        {/* Hero (8/12) + Sidebar (4/12) */}
        <div className="grid gap-6 grid-cols-1 lg:grid-cols-12 lg:items-stretch">
          {/* Hero skeleton */}
          <Card className="lg:col-span-8 border-border/60 flex flex-col">
            <CardContent className="p-6 md:p-8 space-y-6 flex-1">
              <div className="flex items-start justify-between gap-4">
                <div className="h-3 w-36 bg-muted rounded animate-pulse" />
                <div className="h-5 w-20 bg-muted rounded-full animate-pulse" />
              </div>
              <div className="space-y-2">
                <div className="h-12 w-56 bg-muted rounded animate-pulse" />
                <div className="h-7 w-32 bg-muted rounded animate-pulse" />
              </div>
              <div className="h-px bg-muted rounded" />
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 bg-muted rounded-lg animate-pulse shrink-0" />
                <div className="space-y-1.5 flex-1">
                  <div className="h-3 w-24 bg-muted rounded animate-pulse" />
                  <div className="h-4 w-48 bg-muted rounded animate-pulse" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sidebar skeleton */}
          <Card className="lg:col-span-4 border-border/60 flex flex-col">
            <CardContent className="p-2 flex-1">
              <div className="px-3 pt-3 pb-4">
                <div className="h-3 w-16 bg-muted rounded animate-pulse" />
              </div>
              <div className="space-y-1">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-3">
                    <div className="h-9 w-9 bg-muted rounded-lg animate-pulse shrink-0" />
                    <div className="space-y-1.5 flex-1">
                      <div className="h-3.5 w-28 bg-muted rounded animate-pulse" />
                      <div className="h-3 w-36 bg-muted rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI usage strip skeleton */}
        <Card className="border-border/60">
          <CardContent className="p-6 md:p-8">
            <div className="grid gap-6 md:grid-cols-12 items-start">
              <div className="md:col-span-5 space-y-3">
                <div className="h-3 w-40 bg-muted rounded animate-pulse" />
                <div className="h-14 w-28 bg-muted rounded animate-pulse" />
                <div className="h-3 w-24 bg-muted rounded animate-pulse" />
              </div>
              <div className="md:col-span-7 space-y-4">
                <div className="h-2.5 w-full bg-muted rounded-full animate-pulse" />
                <div className="grid gap-3 sm:grid-cols-2">
                  {[0, 1].map((i) => (
                    <div key={i} className="flex items-start gap-2.5">
                      <div className="h-4 w-4 bg-muted rounded animate-pulse shrink-0 mt-0.5" />
                      <div className="space-y-1.5 flex-1">
                        <div className="h-2.5 w-20 bg-muted rounded animate-pulse" />
                        <div className="h-4 w-28 bg-muted rounded animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Derived state for the modern hero / stat grid
  const effectiveStatus = getEffectiveSubscriptionStatus();
  const statusLabel =
    effectiveStatus === "active"
      ? t("dashboard.stats.active")
      : effectiveStatus === "trialing"
        ? t("subscription.dashboard.trialStatus")
        : effectiveStatus === "cancelling"
          ? t("subscription.dashboard.cancellingStatus")
          : t("subscription.dashboard.inactive");
  const isActiveLike =
    subscription?.status === "active" ||
    effectiveStatus === "trialing";
  const nextChargeDate = isInTrialPeriod()
    ? subscription?.trialEnd
    : user?.subscriptionEndDate;
  const nextChargeAmount = subscription?.plan
    ? `${subscription.plan.currency}${Number(subscription.plan.price).toFixed(2)}`
    : null;
  const nextChargeInterval =
    subscription?.plan?.billingInterval === "year"
      ? t("plans.perYear")
      : t("subscription.dashboard.monthly");

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 p-4 md:p-6">
      {/* Page header — modern, scannable */}
      <header className="flex flex-col gap-1.5">
        <p className="text-sm font-medium text-muted-foreground">
          {t("subscription.dashboard.billingInformation")}
        </p>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
          {t("subscription.dashboard.currentPlan")}
        </h1>
      </header>

      {/* Inline status banners (trial / cancellation) — modern left-border alerts */}
      {isInTrialPeriod() && (
        <Card className="border-l-4 border-l-blue-500 border-blue-200/50 bg-blue-50/50 dark:bg-blue-950/20 dark:border-blue-900/40 transition-colors duration-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-blue-800 dark:text-blue-200">
              <Zap className="h-5 w-5" />
              <span>{t("subscription.dashboard.trialPeriodActive")}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start space-x-3">
              <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="space-y-2 flex-1">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t("subscription.dashboard.trialEndsOn", {
                    date: subscription?.trialEnd
                      ? formatDate(subscription.trialEnd)
                      : t("subscription.dashboard.unknown"),
                    days: getRemainingTrialDays(),
                  })}
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  {t("subscription.dashboard.trialDescription")}
                </p>
                {subscription?.trialEnd && (
                  <div className="mt-3 p-3 bg-white/50 dark:bg-black/20 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        {t("subscription.dashboard.daysRemaining")}
                      </span>
                      <Badge className="bg-blue-600 text-white">
                        {getRemainingTrialDays()}{" "}
                        {t("subscription.dashboard.days")}
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cancellation Warning — modern left-border alert */}
      {isSubscriptionCancellingAtPeriodEnd() && (
        <Card className="border-l-4 border-l-orange-500 border-orange-200/50 bg-orange-50/50 dark:bg-orange-950/20 dark:border-orange-900/40 transition-colors duration-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center space-x-2 text-orange-800 dark:text-orange-200">
              <AlertTriangle className="h-5 w-5" />
              <span>{t("subscription.dashboard.subscriptionCancelling")}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start space-x-3">
              <Clock className="h-5 w-5 text-orange-600 mt-0.5" />
              <div className="space-y-2 flex-1">
                <p className="text-sm text-orange-800 dark:text-orange-200">
                  {t("subscription.dashboard.subscriptionWillBeCancelledOn", {
                    date: subscription?.endDate
                      ? formatDate(subscription.endDate)
                      : t("subscription.dashboard.endOfPeriod"),
                  })}
                </p>
                <p className="text-xs text-orange-700 dark:text-orange-300">
                  {t("subscription.dashboard.canReactivateAnytime")}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={handleReactivateSubscription}
                    disabled={isReactivatingSubscription}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    size="sm"
                  >
                    {isReactivatingSubscription ? (
                      <>
                        <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                        {t("subscription.dashboard.reactivating")}
                      </>
                    ) : (
                      t("subscription.dashboard.reactivateSubscription")
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isActiveLike ? (
        <>
          {/* Hero (left, 8/12) + Quick Actions sidebar (right, 4/12) */}
          <section
            aria-label={t("subscription.dashboard.currentPlan")}
            className="grid gap-6 grid-cols-1 lg:grid-cols-12 lg:items-stretch"
          >
            {/* HERO — plan name + price as the visual centerpiece */}
            <div className="lg:col-span-8 flex flex-col">
              <Card className="overflow-hidden border-border/60 flex-1">
                <CardContent className="p-6 md:p-8 space-y-6">
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      {t("subscription.dashboard.currentSubscription")}
                    </p>
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium",
                        effectiveStatus === "active" &&
                          "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
                        effectiveStatus === "trialing" &&
                          "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
                        effectiveStatus === "cancelling" &&
                          "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
                        !["active", "trialing", "cancelling"].includes(
                          effectiveStatus ?? "",
                        ) && "bg-muted text-muted-foreground",
                      )}
                    >
                      <span
                        className={cn(
                          "h-1.5 w-1.5 rounded-full",
                          getEffectiveSubscriptionStatusColor(),
                        )}
                        aria-hidden="true"
                      />
                      {statusLabel}
                    </span>
                  </div>

                  <div className="space-y-2">
                    <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-none truncate">
                      {user?.subscriptionPlan ||
                        t("dashboard.subscription.freePlan")}
                    </h2>
                    {nextChargeAmount && (
                      <p className="text-2xl font-semibold text-muted-foreground tabular-nums">
                        {nextChargeAmount}
                        <span className="ml-1 text-base font-normal">
                          / {nextChargeInterval}
                        </span>
                      </p>
                    )}
                  </div>

                  <Separator />

                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        "flex items-center justify-center h-9 w-9 rounded-lg shrink-0",
                        isInTrialPeriod()
                          ? "bg-blue-100 dark:bg-blue-900/30"
                          : "bg-emerald-100 dark:bg-emerald-900/30",
                      )}
                      aria-hidden="true"
                    >
                      <Calendar
                        className={cn(
                          "h-4 w-4",
                          isInTrialPeriod()
                            ? "text-blue-600 dark:text-blue-400"
                            : "text-emerald-600 dark:text-emerald-400",
                        )}
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        {isInTrialPeriod()
                          ? t("subscription.dashboard.firstPayment")
                          : t("subscription.dashboard.nextPayment")}
                      </p>
                      <p className="text-sm font-medium mt-0.5">
                        {nextChargeDate
                          ? formatDateLong(nextChargeDate)
                          : t("subscription.dashboard.notScheduled")}
                      </p>
                      {isInTrialPeriod() && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                          {t("subscription.dashboard.afterTrial")}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* SIDEBAR — quick actions */}
            <aside
              className="lg:col-span-4 flex flex-col"
              aria-label={t("subscription.dashboard.actions")}
            >
              <Card className="border-border/60 flex-1">
                <CardContent className="p-2">
                  <p className="px-3 pt-3 pb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("subscription.dashboard.actions")}
                  </p>

                  {!isInTrialPeriod() && (
                    <button
                      type="button"
                      onClick={handleUpgradePlan}
                      disabled={isLoading}
                      className="group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left cursor-pointer transition-colors duration-150 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span
                        className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0"
                        aria-hidden="true"
                      >
                        <Crown className="h-4 w-4" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium truncate">
                          {t("subscription.dashboard.upgradePlan")}
                        </span>
                        <span className="block text-xs text-muted-foreground truncate">
                          {t("subscription.dashboard.planDetails")}
                        </span>
                      </span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={handleViewBillingHistory}
                    className="group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left cursor-pointer transition-colors duration-150 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground shrink-0"
                      aria-hidden="true"
                    >
                      <History className="h-4 w-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">
                        {t("subscription.dashboard.viewBillingHistory")}
                      </span>
                      <span className="block text-xs text-muted-foreground truncate">
                        {t("subscription.dashboard.billingInformationDescription")}
                      </span>
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={handleUpdatePayment}
                    className="group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left cursor-pointer transition-colors duration-150 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span
                      className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 shrink-0"
                      aria-hidden="true"
                    >
                      <CreditCard className="h-4 w-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">
                        {t("subscription.dashboard.paymentMethod")}
                      </span>
                      <span className="block text-xs text-muted-foreground tabular-nums truncate">
                        Visa •••• 4242
                      </span>
                    </span>
                  </button>

                  {!isSubscriptionCancellingAtPeriodEnd() && (
                    <>
                      <div className="my-1 mx-3 border-t border-border/60" />
                      <button
                        type="button"
                        onClick={() => setIsCancelDialogOpen(true)}
                        className="group flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left cursor-pointer transition-colors duration-150 hover:bg-destructive/10 text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive"
                      >
                        <span
                          className="flex h-9 w-9 items-center justify-center rounded-lg bg-destructive/10 shrink-0"
                          aria-hidden="true"
                        >
                          <X className="h-4 w-4" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-medium truncate">
                            {t("subscription.dashboard.cancelSubscription")}
                          </span>
                        </span>
                      </button>
                    </>
                  )}
                </CardContent>
              </Card>
            </aside>
          </section>

          {/* AI usage — full-width strip under the hero */}
          <section aria-label="Assistant IA" className="space-y-6">
            <AiUsageCard />
            <AiCreditsSection />
          </section>

          {/* Factures — section visible (le dialog complet reste accessible
              via « Voir tout ») : une clientèle de cabinets vit de factures,
              elles ne doivent pas être cachées derrière un lien texte. */}
          <section aria-label="Factures">
            <Card className="border-border/60">
              <CardContent className="p-6 md:p-8 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <FileText
                      className="h-4 w-4 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      {t("subscription.dashboard.recentInvoices")}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="cursor-pointer"
                    onClick={handleViewBillingHistory}
                  >
                    {t("subscription.dashboard.viewAllInvoices")}
                  </Button>
                </div>

                {isLoadingInvoices && invoices.length === 0 ? (
                  <div className="space-y-2" aria-busy="true">
                    {[0, 1].map((i) => (
                      <div key={i} className="h-10 bg-muted rounded animate-pulse" />
                    ))}
                  </div>
                ) : invoices.length === 0 ? (
                  <p className="py-4 text-sm text-muted-foreground">
                    {t("subscription.dashboard.invoicesWillAppearHere")}
                  </p>
                ) : (
                  <ul className="divide-y divide-border/60 rounded-lg border border-border/60">
                    {invoices.slice(0, 5).map((invoice) => (
                      <li
                        key={invoice.id}
                        className="flex flex-wrap items-center justify-between gap-3 px-4 py-2.5 text-sm"
                      >
                        <span className="font-medium truncate min-w-0">
                          {getInvoiceDisplayNumber(invoice)}
                        </span>
                        <span className="text-muted-foreground whitespace-nowrap">
                          {new Date(invoice.created * 1000).toLocaleDateString(
                            "fr-FR",
                            { year: "numeric", month: "short", day: "numeric" },
                          )}
                        </span>
                        <span className="tabular-nums font-medium whitespace-nowrap">
                          {formatCurrency(invoice.amount_paid, invoice.currency)}
                        </span>
                        <Badge
                          variant={
                            invoice.status === "paid"
                              ? "default"
                              : invoice.status === "open"
                                ? "secondary"
                                : "destructive"
                          }
                          className="capitalize"
                        >
                          {invoice.status}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          className="cursor-pointer"
                          onClick={() => handleDownloadInvoice(invoice.id)}
                          disabled={!invoice.invoice_pdf}
                        >
                          <Download className="h-3 w-3 mr-1" aria-hidden="true" />
                          PDF
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </section>
        </>
      ) : (
        // Empty state — no active subscription
        <Card>
          <CardContent className="p-8 md:p-12 text-center">
            <div
              className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted"
              aria-hidden="true"
            >
              <Crown className="h-7 w-7 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold tracking-tight">
              {t("subscription.dashboard.noActiveSubscription")}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
              {t("subscription.dashboard.planDetails")}
            </p>
            <Button
              onClick={() => navigate("/plans")}
              className="mt-5 transition-colors duration-150"
            >
              <Crown className="h-4 w-4 mr-2" />
              {t("subscription.dashboard.choosePlan")}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {t("subscription.dashboard.cancelSubscriptionTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("subscription.dashboard.cancelSubscriptionDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              {t("subscription.dashboard.cancelSubscriptionWarning")}
            </p>
            {subscription && (
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="font-medium">
                    {t("subscription.dashboard.currentPlan")}:
                  </span>
                  <span>
                    {subscription.plan?.name ||
                      user?.subscriptionPlan ||
                      t("subscription.dashboard.unknownPlan")}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="font-medium">
                    {t("subscription.dashboard.validUntil")}:
                  </span>
                  <span>
                    {subscription.endDate
                      ? formatDate(subscription.endDate)
                      : "N/A"}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCancelDialogOpen(false)}
              disabled={isUpdatingSubscription}
            >
              {t("subscription.dashboard.keepSubscription")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={isUpdatingSubscription}
            >
              {isUpdatingSubscription ? (
                <>
                  <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                  {t("subscription.dashboard.cancelling")}
                </>
              ) : (
                t("subscription.dashboard.confirmCancel")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Subscription Dialog */}
      <Dialog open={isUpgradeDialogOpen} onOpenChange={setIsUpgradeDialogOpen}>
        <DialogContent className="sm:max-w-[600px] scrollbar-thin dark-scrollbar scrollbar-minimal">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Crown className="h-5 w-5 text-violet-600" />
              <span>
                {t("subscription.dashboard.upgradeSubscriptionTitle")}
              </span>
            </DialogTitle>
            <DialogDescription>
              {t("subscription.dashboard.upgradeSubscriptionDescription")}
            </DialogDescription>
          </DialogHeader>

          <div
            className="py-4 max-h-[60vh] overflow-y-auto scrollbar-thin dark-scrollbar scrollbar-minimal"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(156, 163, 175, 0.4) transparent",
            }}
          >
            {isLoadingUpgradePlans ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-violet-600 border-t-transparent rounded-full" />
                <span className="ml-2">
                  {t("subscription.dashboard.loadingUpgradePlans")}
                </span>
              </div>
            ) : availableUpgradePlans.length === 0 ? (
              <div className="text-center py-8">
                <Crown className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  {t("subscription.dashboard.alreadyHighestPlan")}
                </p>
              </div>
            ) : (
              <div
                className="space-y-4 scrollbar-thin dark-scrollbar scrollbar-minimal overflow-y-auto"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(156, 163, 175, 0.4) transparent",
                }}
              >
                {availableUpgradePlans.map((plan) => (
                  <div
                    key={plan.id}
                    className="border rounded-lg p-4 hover:border-violet-300 transition-colors cursor-pointer"
                    onClick={() => handleSelectUpgradePlan(plan)}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center">
                          {plan.name}
                          {plan.isPopular && (
                            <Badge className="ml-2 bg-violet-500">
                              <Star className="w-3 h-3 mr-1" />
                              {t("subscription.dashboard.popular")}
                            </Badge>
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {plan.description}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-violet-600">
                          {plan.currency}
                          {plan.price}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {t("subscription.dashboard.perMonth")}
                        </div>
                      </div>
                    </div>

                    {/* Plan highlights */}
                    <div className="mb-4">
                      {/* All plan features */}
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                          {t("subscription.dashboard.allBenefitsIncluded")}
                        </h4>
                        <ul className="space-y-1.5">
                          {plan.features.map((feature, index) => (
                            <li
                              key={index}
                              className="flex items-start text-sm"
                            >
                              <div className="flex-shrink-0 w-4 h-4 rounded-full bg-green-500 flex items-center justify-center mt-0.5 mr-3">
                                <svg
                                  className="w-2.5 h-2.5 text-white"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                              <span className="text-gray-700 dark:text-gray-300 leading-relaxed">
                                {feature}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <Button
                      className="w-full mt-2 bg-violet-600 hover:bg-violet-700"
                      onClick={() => handleSelectUpgradePlan(plan)}
                      disabled={isLoadingProration || isProcessingUpgrade}
                    >
                      {isLoadingProration ? (
                        <>
                          <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                          Calcul de la proration...
                        </>
                      ) : (
                        <>
                          <Crown className="h-4 w-4 mr-2" />
                          {t("subscription.dashboard.choosePlanAction")}
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsUpgradeDialogOpen(false)}
            >
              {t("subscription.dashboard.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proration Preview Dialog */}
      <Dialog
        open={isProrationDialogOpen}
        onOpenChange={setIsProrationDialogOpen}
      >
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Crown className="h-5 w-5 text-violet-600" />
              <span>{t("subscription.dashboard.prorationPreviewTitle")}</span>
            </DialogTitle>
            <DialogDescription>
              {t("subscription.dashboard.prorationPreviewDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-6">
            {prorationDetails && (
              <>
                {/* Plan Information */}
                <div className="bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-4">
                  <h3 className="font-semibold text-violet-800 dark:text-violet-200 mb-3 flex items-center">
                    <Crown className="h-4 w-4 mr-2" />
                    {t("subscription.dashboard.planChangeTitle")}
                  </h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">
                        {t("subscription.dashboard.currentPlanLabel")}
                      </span>
                      <p className="font-medium">{user?.subscriptionPlan}</p>
                      <p className="text-xs text-muted-foreground">
                        {prorationDetails.current_plan &&
                          formatCurrency(
                            prorationDetails.current_plan.amount,
                            prorationDetails.current_plan.currency,
                          )}
                        /{t("subscription.dashboard.perMonth")}
                      </p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">
                        {t("subscription.dashboard.newPlanLabel")}
                      </span>
                      <p className="font-medium text-violet-600">
                        {selectedUpgradePlan?.name}
                      </p>
                      <p className="text-xs text-violet-600">
                        {prorationDetails.new_plan &&
                          formatCurrency(
                            prorationDetails.new_plan.amount,
                            prorationDetails.new_plan.currency,
                          )}
                        /{t("subscription.dashboard.perMonth")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Billing Period Information */}
                {prorationDetails.billing_period && (
                  <div className="border rounded-lg p-4">
                    <h3 className="font-semibold mb-3 flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {t("subscription.dashboard.currentBillingPeriod")}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>{t("subscription.dashboard.totalPeriod")}</span>
                        <span>
                          {prorationDetails.billing_period.total_days}{" "}
                          {t("subscription.dashboard.days")}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t("subscription.dashboard.daysUsed")}</span>
                        <span>
                          {prorationDetails.billing_period.used_days}{" "}
                          {t("subscription.dashboard.days")}
                        </span>
                      </div>
                      <div className="flex justify-between text-violet-600">
                        <span>{t("subscription.dashboard.daysRemaining")}</span>
                        <span className="font-medium">
                          {prorationDetails.billing_period.remaining_days}{" "}
                          {t("subscription.dashboard.days")}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t("subscription.dashboard.nextBilling")}</span>
                        <span>
                          {new Date(
                            prorationDetails.billing_period.next_billing_date,
                          ).toLocaleDateString("fr-FR")}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 italic">
                      {t("subscription.dashboard.newAnniversaryNote")}
                    </p>
                  </div>
                )}

                {/* Special case message for subscriptions without billing periods */}
                {!prorationDetails.billing_period && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <h3 className="font-semibold mb-2 flex items-center text-yellow-800 dark:text-yellow-200">
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      {t("subscription.dashboard.specialSubscriptionState")}
                    </h3>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      {t(
                        "subscription.dashboard.specialSubscriptionStateDescription",
                      )}
                    </p>
                  </div>
                )}

                {/* Proration Calculation */}
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-3 flex items-center">
                    <CreditCard className="h-4 w-4 mr-2" />
                    {t("subscription.dashboard.prorationCalculationTitle")}
                  </h3>
                  <div className="space-y-3">
                    {prorationDetails.proration_preview?.lines?.map(
                      (line: any, index: number) => (
                        <div
                          key={index}
                          className={`flex justify-between items-center p-2 rounded ${
                            line.proration
                              ? "bg-green-50 dark:bg-green-900/20"
                              : "bg-blue-50 dark:bg-blue-900/20"
                          }`}
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {line.description}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {line.proration
                                ? t("subscription.dashboard.automaticCredit")
                                : t("subscription.dashboard.immediateBilling")}
                            </p>
                          </div>
                          <span
                            className={`font-semibold ${
                              line.amount < 0
                                ? "text-green-600"
                                : "text-blue-600"
                            }`}
                          >
                            {formatCurrency(
                              Math.abs(line.amount),
                              prorationDetails.proration_preview.currency,
                            )}
                            {line.amount < 0 &&
                              ` ${t("subscription.dashboard.credit")}`}
                          </span>
                        </div>
                      ),
                    ) || []}

                    <div className="border-t pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">
                          {t("subscription.dashboard.amountToPayNow")}
                        </span>
                        <span className="text-lg font-bold text-violet-600">
                          {(prorationDetails.proration_preview?.amount_due ||
                            0) > 0
                            ? formatCurrency(
                                prorationDetails.proration_preview.amount_due,
                                prorationDetails.proration_preview.currency,
                              )
                            : t("subscription.dashboard.free")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Billing Explanation */}
                {prorationDetails.proration_preview?.explanation && (
                  <div
                    className={`border rounded-lg p-4 ${
                      prorationDetails.proration_preview.explanation
                        .immediate_charge
                        ? "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800"
                        : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                    }`}
                  >
                    <h3 className="font-semibold mb-2 flex items-center">
                      <Clock className="h-4 w-4 mr-2" />
                      {t("subscription.dashboard.billingExplanationTitle")}
                    </h3>
                    <p className="text-sm leading-relaxed mb-3">
                      {
                        prorationDetails.proration_preview.explanation
                          .billing_explanation
                      }
                    </p>

                    <div className="text-xs space-y-1 bg-white/50 dark:bg-black/20 rounded p-2">
                      <p>
                        <strong>📅 {t("subscription.dashboard.today")}</strong>{" "}
                        {(prorationDetails.proration_preview?.amount_due || 0) >
                        0
                          ? `${t("subscription.dashboard.chargeOf")} ${formatCurrency(
                              prorationDetails.proration_preview.amount_due,
                              prorationDetails.proration_preview.currency,
                            )}`
                          : t("subscription.dashboard.noChargeCredit")}
                      </p>
                      <p>
                        <strong>
                          📅{" "}
                          {new Date(
                            prorationDetails.proration_preview.explanation
                              .next_full_charge_date,
                          ).toLocaleDateString("fr-FR")}{" "}
                          :
                        </strong>{" "}
                        {t("subscription.dashboard.normalMonthlyCharge")}{" "}
                        {formatCurrency(
                          prorationDetails.proration_preview.explanation
                            .next_full_charge_amount,
                          prorationDetails.proration_preview.currency,
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Security Note */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <p className="text-sm text-blue-800 dark:text-blue-200 flex items-start">
                    <Shield className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      {(prorationDetails.proration_preview?.amount_due || 0) > 0
                        ? t("subscription.dashboard.securityNote")
                        : t("subscription.dashboard.securityNoteNoPayment")}
                    </span>
                  </p>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsProrationDialogOpen(false)}
              disabled={isProcessingUpgrade}
            >
              {t("subscription.dashboard.cancel")}
            </Button>
            <Button
              onClick={handleConfirmUpgrade}
              disabled={isProcessingUpgrade}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {isProcessingUpgrade ? (
                <>
                  <div className="animate-spin h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full" />
                  {(prorationDetails?.proration_preview?.amount_due || 0) > 0
                    ? t("subscription.dashboard.redirecting")
                    : t("subscription.dashboard.upgrading")}
                </>
              ) : (
                <>
                  {(prorationDetails?.proration_preview?.amount_due || 0) >
                  0 ? (
                    <>
                      <CreditCard className="h-4 w-4 mr-2" />
                      {t("subscription.dashboard.confirmAndPay")}
                    </>
                  ) : (
                    <>
                      <Crown className="h-4 w-4 mr-2" />
                      {t("subscription.dashboard.confirmUpgrade")}
                    </>
                  )}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Billing History Dialog */}
      <Dialog
        open={isBillingHistoryDialogOpen}
        onOpenChange={setIsBillingHistoryDialogOpen}
      >
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <History className="h-5 w-5 text-blue-600" />
              <span>{t("subscription.dashboard.billingHistoryTitle")}</span>
            </DialogTitle>
            <DialogDescription>
              {t("subscription.dashboard.billingHistoryDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {isLoadingInvoices ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full" />
                <span className="ml-2">
                  {t("subscription.dashboard.loadingBillingHistory")}
                </span>
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-8">
                <History className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground mb-2">
                  {t("subscription.dashboard.noBillingHistoryFound")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("subscription.dashboard.invoicesWillAppearHere")}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">
                    {t("subscription.dashboard.recentInvoices")}
                  </h3>
                  <Badge variant="secondary">
                    {invoices.length}{" "}
                    {t("subscription.dashboard.invoiceCount", {
                      count: invoices.length,
                    })}
                  </Badge>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          {t("subscription.dashboard.invoiceNumber")}
                        </TableHead>
                        <TableHead>
                          {t("subscription.dashboard.date")}
                        </TableHead>
                        <TableHead>
                          {t("subscription.dashboard.amount")}
                        </TableHead>
                        <TableHead>
                          {t("subscription.dashboard.invoiceStatus")}
                        </TableHead>
                        <TableHead className="text-right">
                          {t("subscription.dashboard.actions")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-medium">
                            {getInvoiceDisplayNumber(invoice)}
                          </TableCell>
                          <TableCell>
                            {new Date(
                              invoice.created * 1000,
                            ).toLocaleDateString("fr-FR", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">
                              {formatCurrency(
                                invoice.amount_paid,
                                invoice.currency,
                              )}
                            </div>
                            {invoice.amount_due > 0 && (
                              <div className="text-xs text-muted-foreground">
                                {t("subscription.dashboard.due")}:{" "}
                                {formatCurrency(
                                  invoice.amount_due,
                                  invoice.currency,
                                )}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                invoice.status === "paid"
                                  ? "default"
                                  : invoice.status === "open"
                                    ? "secondary"
                                    : "destructive"
                              }
                              className="capitalize"
                            >
                              {invoice.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  handleDownloadInvoice(invoice.id)
                                }
                                disabled={!invoice.invoice_pdf}
                              >
                                <Download className="h-3 w-3 mr-1" />
                                {t("subscription.dashboard.downloadPDF")}
                              </Button>
                              {invoice.hosted_invoice_url && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    window.open(
                                      invoice.hosted_invoice_url,
                                      "_blank",
                                    )
                                  }
                                >
                                  {t("subscription.dashboard.viewInvoice")}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {invoices.length > 0 && (
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                      {t("subscription.dashboard.showingRecentInvoices", {
                        count: invoices.length,
                      })}
                    </span>
                    <span>
                      {t("subscription.dashboard.totalPaid")}{" "}
                      {formatCurrency(
                        invoices.reduce((sum, inv) => sum + inv.amount_paid, 0),
                        invoices[0]?.currency || "eur",
                      )}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsBillingHistoryDialogOpen(false)}
            >
              {t("subscription.dashboard.close")}
            </Button>
            {invoices.length > 0 && (
              <Button
                onClick={handleManageBilling}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {t("subscription.dashboard.manageBillingAction")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
